import React, { useState } from 'react';
import { apiService } from '@/services/api';
import { otoPrograms, otoEnrollPayload, OTO_COPY, OTO_PRACTICE } from '@/data/conciergeOto';
import { fmtUsd } from '@/lib/followupQuote';

// §concierge-oto — the post-payment one-time-offer page.
//
// Rendered between "visit created" and the confirmation screen for eligible
// (New York) treatment patients. Three concierge programs, each with its price
// on the button and ONE click to enrol: POST /concierge/oto/enroll charges the
// card on file and creates the concierge enrolment from the visit's data.
//
// `preview` renders the page with inert buttons (the /concierge-offer-preview
// route), so the design can be reviewed without a visit or a backend.
const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const Check = () => (
  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

function describeEnrollError(err) {
  const d = (err && err.response && err.response.data) || {};
  const code = String(d.code || d.errorCode || '').toLowerCase();
  // BE #347: a failure AFTER the charge is refunded the same day.
  if (d.refunded === true) return "We couldn't add this program and refunded you. You can enrol later from the program page.";
  if (code === 'card_declined' || code === 'payment_failed') return 'Your card was declined for this add-on. Nothing was charged — you can enrol later from the program page.';
  if (code === 'no_card_on_file') return "We couldn't find a card on file for one-click enrolment. Nothing was charged — you can enrol from the program page.";
  if (code === 'already_enrolled') return "You're already enrolled in this program.";
  if (code === 'state_not_served') return 'This program is only available to New York patients.';
  if (code === 'not_offerable') return "This add-on isn't available for this visit. Nothing was charged.";
  if (code === 'plan_not_configured') return "This program can't be added right now. Nothing was charged — you can enrol later from the program page.";
  if (code === 'price_mismatch') return 'The price changed while this page was open. Nothing was charged — please reopen your confirmation.';
  // BE #347: the consent text changed since the offer was fetched; the gate
  // re-fetches it and the patient clicks again against the current text.
  if (code === 'consent_changed') return 'The terms were updated while this page was open. Nothing was charged — please review the updated terms below and click again.';
  return d.message || d.msg || "We couldn't add this right now. Nothing was charged — you can enrol later from the program page.";
}

// `offer`: GET /concierge/oto/offer, normalised ({ [program]: {todayCents, monthlyCents, consentText, consentSha256} }).
// Buttons are priced from it and its consentText is shown VERBATIM next to each
// button (the server records that text as the consent given on click).
// `hide`: programs the patient already holds (payment response alreadyEnrolled).
// `onRefreshOffer`: ask the gate to re-fetch the offer (422 consent_changed).
const ConciergeOtoStep = ({ answers = {}, visitType = '', visitResult = null, paymentResult = null, onContinue, preview = false, offer = null, hide = [], onRefreshOffer }) => {
  const programs = otoPrograms({ offer, hide });
  const [state, setState] = useState({}); // key → { status: 'adding'|'added'|'error', message?, result? }
  const anyAdded = Object.values(state).some((s) => s.status === 'added');
  const firstName = (answers['basic-contact']?.name || '').trim().split(/\s+/)[0] || '';

  // Nothing left to offer (all programs already held) → straight to the confirmation.
  React.useEffect(() => {
    if (!preview && programs.length === 0 && typeof onContinue === 'function') onContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs.length]);

  const add = async (p) => {
    if (state[p.key]?.status === 'adding' || state[p.key]?.status === 'added') return;
    if (preview) {
      setState((s) => ({ ...s, [p.key]: { status: 'added', message: `Preview — would charge ${fmtUsd(p.todayCents)} to the card on file.` } }));
      return;
    }
    setState((s) => ({ ...s, [p.key]: { status: 'adding' } }));
    try {
      const body = otoEnrollPayload({ program: p.key, visitResult, paymentResult, visitType, answers, offerProgram: offer ? offer[p.key] : null });
      const res = await apiService.enrollConciergeOto(body);
      setState((s) => ({ ...s, [p.key]: { status: 'added', result: res } }));
    } catch (err) {
      const code = String(err?.response?.data?.code || err?.response?.data?.errorCode || '').toLowerCase();
      if (code === 'already_enrolled') {
        setState((s) => ({ ...s, [p.key]: { status: 'added' } }));
        return;
      }
      if (code === 'consent_changed' && typeof onRefreshOffer === 'function') onRefreshOffer();
      console.error('[concierge-oto] enroll failed', err);
      setState((s) => ({ ...s, [p.key]: { status: 'error', message: describeEnrollError(err) } }));
    }
  };

  return (
    <div data-testid="concierge-oto" className="max-w-5xl mx-auto">
      {/* Progress framing: the order is done; this is an optional step 2. */}
      <div className="flex items-center justify-center gap-3 text-xs font-medium text-gray-500 mb-5" data-testid="oto-progress">
        <span className="inline-flex items-center gap-1 text-[#2d8a5f]"><Check /> Order complete</span>
        <span className="h-px w-8 bg-gray-200" />
        <span className="text-gray-900">Add concierge care (optional)</span>
        <span className="h-px w-8 bg-gray-200" />
        <span>Confirmation</span>
      </div>

      <div className="text-center mb-8">
        <span className="inline-block text-xs font-semibold tracking-wide uppercase text-[#2d8a5f] bg-[#e8f5ee] rounded-full px-3 py-1 mb-3" data-testid="oto-ny-badge">
          {OTO_COPY.nyOnly}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          {firstName ? `${firstName}, one more thing before you go.` : OTO_COPY.headline}
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">{OTO_COPY.subhead}</p>
      </div>

      {/* Doctor strip */}
      <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-2xl p-4 mb-6 max-w-2xl mx-auto">
        <img src={OTO_PRACTICE.image} alt={OTO_PRACTICE.practitioner} className="w-14 h-14 rounded-full object-cover flex-shrink-0" loading="lazy" />
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{OTO_PRACTICE.practitioner}</p>
          <p className="text-sm text-gray-500 truncate">{OTO_PRACTICE.name}</p>
        </div>
      </div>

      {/* The three offers */}
      <div className="grid md:grid-cols-3 gap-5 mb-6" data-testid="oto-programs">
        {programs.map((p) => {
          const st = state[p.key] || {};
          const added = st.status === 'added';
          const adding = st.status === 'adding';
          return (
            <div
              key={p.key}
              className={`flex flex-col bg-white border-2 rounded-2xl overflow-hidden transition-all ${added ? 'border-[#2d8a5f] shadow-md' : 'border-gray-200'}`}
              data-testid={`oto-card-${p.key}`}
            >
              <div className="relative h-36 overflow-hidden bg-gray-100">
                <img src={p.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wide bg-white/90 text-gray-800 rounded-full px-2.5 py-1">{p.eyebrow}</span>
              </div>
              <div className="flex flex-col flex-1 p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{p.name}</h3>
                <p className="text-2xl font-bold text-[#2d8a5f] mb-2" data-testid={`oto-price-${p.key}`}>{p.priceLabel}</p>
                <p className="text-sm text-gray-600 mb-3">{p.tagline}</p>
                <ul className="space-y-1.5 mb-4 text-sm text-gray-700">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2"><Check /><span>{h}</span></li>
                  ))}
                </ul>
                <div className="mt-auto">
                  {added ? (
                    <div className="rounded-xl bg-[#e8f5ee] border border-[#2d8a5f]/30 p-3 text-sm text-gray-800" data-testid={`oto-added-${p.key}`}>
                      <p className="font-semibold text-[#2d8a5f]">Added ✓</p>
                      <p>{st.message || `${OTO_PRACTICE.practitionerShort}'s team will reach out within one business day.`}</p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => add(p)}
                      disabled={adding}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#2d8a5f] hover:bg-[#25714e] px-4 py-3 font-semibold text-white disabled:opacity-60 transition-colors"
                      data-testid={`oto-add-${p.key}`}
                    >
                      {adding ? <><Spinner /> Adding…</> : p.buttonLabel}
                    </button>
                  )}
                  {st.status === 'error' && (
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`oto-error-${p.key}`}>{st.message}</p>
                  )}
                  <p className="mt-2 text-[11px] text-gray-400">
                    {p.monthlyCents !== p.todayCents
                      ? `${fmtUsd(p.todayCents)} today, then ${fmtUsd(p.monthlyCents)}/month. `
                      : `${fmtUsd(p.todayCents)} today, then monthly. `}
                    One click — charged to the card you just used.
                  </p>
                  {p.consentText && (
                    <p className="mt-2 text-[11px] leading-snug text-gray-500 whitespace-pre-line" data-testid={`oto-consent-${p.key}`}>
                      {p.consentText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* How it works / trust */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 max-w-3xl mx-auto" data-testid="oto-how">
        <p className="text-sm font-semibold text-gray-900 mb-2">How it works</p>
        <ul className="space-y-1.5 text-sm text-gray-700">
          {OTO_COPY.howItWorks.map((t) => (
            <li key={t} className="flex items-start gap-2"><Check /><span>{t}</span></li>
          ))}
        </ul>
        <p className="text-xs text-gray-500 mt-3">{OTO_COPY.oneTime}</p>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onContinue?.({ enrolled: anyAdded })}
          className={anyAdded
            ? 'inline-flex items-center justify-center rounded-xl bg-[#2d8a5f] hover:bg-[#25714e] px-6 py-3 font-semibold text-white transition-colors'
            : 'text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2'}
          data-testid="oto-continue"
        >
          {anyAdded ? OTO_COPY.continueAfterAdd : OTO_COPY.decline}
        </button>
      </div>
    </div>
  );
};

export default ConciergeOtoStep;
